﻿import controller from '../../../../Resources/Scripts/_Shared/Controller';

import React, { useContext, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import URLSearchParams from '@ungap/url-search-params'
import { pushToDataLayer } from '../../../../Resources/Scripts/_Shared/dataLayer'
import { isInViewport } from '../../../../Resources/Scripts/_Shared/viewport'

import { BookingFlowContextProvider, BookingFlowContext } from './BookingFlowContext'
import { facility } from './Endpoints'
import { Error } from '../../../../Resources/Scripts/_Shared/elements/Error'

import OnlineBooking from './steps/OnlineBooking'
import Login from './steps/Login'
import CreateAccount from './steps/CreateAccount'
import AddVehicle from './steps/AddVehicle'

import OrderOverview from './steps/OrderOverview'
import OrderConfirmation from './steps/OrderConfirmation'
import { ProductLock } from './partials/ProductLock';

function bookingFlowPage(context) {
  const appContainer = context;
  const appConfig = controller.getConfig(context);

  const App = () => {
    const { bookingFlowContext, setBookingFlowContext } = useContext(BookingFlowContext);
    const [pending, setPending] = useState(false);
    const [currentQuoteSessionKey, setCurrentQuoteSessionKey] = useState(null);
    const [error, setError] = useState(false);
    const [reload, setReload] = useState(0);
    const [forceLogin, setForceLogin] = useState(false);
    const topOfContainerRef = useRef(null);
    const selectedLang = document.querySelector('html').getAttribute('lang');

    useEffect(() => {
      let isSubscribed = true;

      const getFacility = async () => {
        if (bookingFlowContext.facility || pending) {
          document.title = bookingFlowContext.facility.name + ' - ' + document.title;
          if (error) {
            setError(false)
            takeUserToStep(0)
          }
          setError(false)
          return;
        }

        setPending(true);
        setError(false);
        
        const urlParams = new URLSearchParams(window.location.search);
        const fId = urlParams.get('fId') ?? urlParams.get('fid');
        const country = document.querySelector('html').getAttribute('country');
        const currentFacility = await facility.getFacility({
          bookingController: bookingFlowContext.episerver.bookingController,
          fId,
          productType: bookingFlowContext.episerver.productType,
          lang: selectedLang === 'nb' ? 'no' : selectedLang,
        },
        bookingFlowContext.useMockedData
      );

        if (currentFacility.status === 200 && isSubscribed) {
          const currentFacilityData = currentFacility.body;
          const facilityData = {
            latitude: currentFacilityData.Address.Marker.Coordinates[1],
            longitude: currentFacilityData.Address.Marker.Coordinates[0],
            maxDistance: 5,
            lang: selectedLang === 'nb' ? 'no' : selectedLang,
            withPmcs: false,
            init: true
          };

          const getNearbyFacilities = await facility.getNearbyFacilities(facilityData);
          const nearbyFacilities = getNearbyFacilities.status === 200 && getNearbyFacilities.body.length ? getNearbyFacilities.body.filter(facility => facility.HasLtp && currentFacilityData.Uid !== facility.Uid) : null;

          setPending(false);
          setBookingFlowContext({
            ...bookingFlowContext,
            country,
            facility: {
              id: currentFacilityData.Uid,
              name: currentFacilityData.FacilityName,
              city: currentFacilityData.Address?.City ?? currentFacilityData.City,
              coordinates: {
                latitude: currentFacilityData.Address.Marker.Coordinates[1],
                longitude: currentFacilityData.Address.Marker.Coordinates[0]
              },
              nearbyFacilities
            },
          })

          document.title = currentFacilityData.FacilityName + ' - ' + document.title;
        } else if (isSubscribed) {
          setPending(false);
          setError(true);
        }

        pushToDataLayer({ event: 'bookingflow_init_load', value: currentFacility.status });
      }
      getFacility();

      return () => isSubscribed = false;
    }, [reload])

    useEffect(() => {
      if (bookingFlowContext.activeStep > 1 && !useMockedLogin && !bookingFlowContext.episerver.loggedIn) setForceLogin(true);

      if (!topOfContainerRef || !topOfContainerRef.current)
        return;

			if (!isInViewport(topOfContainerRef.current)) {
        const mainElement = document.querySelector('.aimo-main');
        if(mainElement)
          mainElement.scrollIntoView({ behavior: 'smooth' });
        else
          topOfContainerRef.current.scrollIntoView({ behavior: 'smooth' });
			}
    }, [bookingFlowContext.activeStep]);

    const takeUserToStep = (stepIndex) => {
      setBookingFlowContext({
        ...bookingFlowContext,
        activeStep: stepIndex
      });
    }

    if (!bookingFlowContext.facility && !error) {
      return (
        <div className="bookingflowpage__loader">
          <div className="aimo-spinner"><span></span><span></span><span></span><span></span></div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="bookingflowpage__error">
          {error.errorMessage ? <Error text={error.errorMessage} /> :
            <div className="bookingflowpage__error__inner">
              <h2>{appConfig.localization.global.errorMessages.load.heading}</h2>
              <p>{appConfig.localization.global.errorMessages.load.text}</p>
              <button onClick={() => { setReload(reload + 1) }}>{appConfig.localization.global.errorMessages.load.button}</button>
            </div>}
        </div>
      )
    }

    const useMockedLogin = (process.env.NODE_ENV !== 'production' && bookingFlowContext.useMockedLogin);
    const loggedInWithActiveProfile = bookingFlowContext.episerver.loggedIn && (!bookingFlowContext.episerver.hasMoreThanOneProfile || bookingFlowContext.episerver.activeProfile);

    return (
      <React.Fragment>
        <div ref={topOfContainerRef}></div>
        <ProductLock 
          bookingFlowContext={bookingFlowContext}
          lang={appConfig.localization.dialog}
          takeUserToStep={takeUserToStep}
          currentQuoteSessionKey={currentQuoteSessionKey}
          setError={setError}
        />
        {bookingFlowContext.activeStep === bookingFlowContext.availableSteps.onlineBooking.index && <OnlineBooking /> }
        {bookingFlowContext.activeStep === bookingFlowContext.availableSteps.login.index && <OnlineBooking /> }
        {bookingFlowContext.activeStep === bookingFlowContext.availableSteps.createAccount.index && ((useMockedLogin || loggedInWithActiveProfile) && !forceLogin ? <CreateAccount /> : <OnlineBooking />) }
        {!pending && bookingFlowContext.activeStep === bookingFlowContext.availableSteps.addVehicle.index && ((useMockedLogin || loggedInWithActiveProfile) && !forceLogin ? <AddVehicle /> : <OnlineBooking />) }
        {bookingFlowContext.activeStep === bookingFlowContext.availableSteps.orderOverview.index && ((useMockedLogin || loggedInWithActiveProfile) && !forceLogin ? <OrderOverview currentQuoteSessionKey={currentQuoteSessionKey} setCurrentQuoteSessionKey={setCurrentQuoteSessionKey} /> : <OnlineBooking />) }
        {bookingFlowContext.activeStep === bookingFlowContext.availableSteps.orderConfirmation.index && ((useMockedLogin || loggedInWithActiveProfile) && !forceLogin ? <OrderConfirmation /> : <OnlineBooking />) }
      </React.Fragment>
    );
  }

  function init() {
    ReactDOM.render(<BookingFlowContextProvider appConfig={appConfig}><App /></BookingFlowContextProvider>, appContainer);
  }

  return {
    init
  };
}

controller.add('booking-flow-page', bookingFlowPage);